/*
 * Original chiptune compositions for the in-game jukebox. Notation: one token
 * per 16th-note step. `C5`/`F#4`/`Bb3` = note, `-` = hold previous note,
 * `.` = rest. Chord tokens (`C`, `Am`, `G7`) stab a chord. Drum tokens combine
 * k (kick), s (snare), h (closed hat), o (open hat). `|` is a bar line.
 */

export interface Song {
  id: string;
  title: string;
  bpm: number;
  /** 0 … ~0.3 — delays every off-beat 16th for a shuffle feel. */
  swing: number;
  lead: string;
  bass: string;
  chords: string;
  drums: string;
  /** Lead instrument duty cycle / flavour. */
  leadWave?: 'pulse25' | 'pulse12' | 'square' | 'triangle';
  eerie?: boolean;
}

const rep = (bar: string, n: number): string => Array.from({ length: n }, () => bar).join(' | ');

const boogie = (
  a: string,
  b: string,
  c: string,
  d: string,
  e: string,
  f: string,
  g: string,
  h: string,
): string => `${a} - ${b} - ${c} - ${d} - ${e} - ${f} - ${g} - ${h} -`;

export const SONGS: Record<string, Song> = {
  grooved: {
    id: 'grooved',
    title: 'Scattered, Smothered & Grooved',
    bpm: 132,
    swing: 0.18,
    leadWave: 'pulse25',
    lead: [
      'G4 . C5 . E5 . G5 - E5 . C5 . D5 - . .',
      'E5 . D5 . C5 . A4 . C5 - - - . . . .',
      'A4 . C5 . F5 . A5 - F5 . C5 . D5 - . .',
      'E5 . G5 . E5 . C5 . D5 - C5 - . . . .',
      'B4 . D5 . G5 . B5 - A5 . G5 . F5 - . .',
      'A5 . G5 . F5 . D5 . C5 - A4 - . . . .',
      'G4 . C5 . E5 . G5 . A5 . G5 . E5 . C5 .',
      'D5 - - . B4 - - . G4 - - - . . . .',
    ].join(' | '),
    bass: [
      boogie('C3', 'E3', 'G3', 'A3', 'Bb3', 'A3', 'G3', 'E3'),
      boogie('C3', 'E3', 'G3', 'A3', 'Bb3', 'A3', 'G3', 'E3'),
      boogie('F2', 'A2', 'C3', 'D3', 'Eb3', 'D3', 'C3', 'A2'),
      boogie('C3', 'E3', 'G3', 'A3', 'Bb3', 'A3', 'G3', 'E3'),
      boogie('G2', 'B2', 'D3', 'E3', 'F3', 'E3', 'D3', 'B2'),
      boogie('F2', 'A2', 'C3', 'D3', 'Eb3', 'D3', 'C3', 'A2'),
      boogie('C3', 'E3', 'G3', 'A3', 'Bb3', 'A3', 'G3', 'E3'),
      boogie('G2', 'B2', 'D3', 'E3', 'F3', 'E3', 'D3', 'B2'),
    ].join(' | '),
    chords: ['C7', 'C7', 'F7', 'C7', 'G7', 'F7', 'C7', 'G7']
      .map((c) => `. . . . ${c} . . . . . . . ${c} . . .`)
      .join(' | '),
    drums: rep('k . h . s . h . k . h k s . h o', 8),
  },
  raisins: {
    id: 'raisins',
    title: 'There Are STILL Raisins In My Toast',
    bpm: 92,
    swing: 0.3,
    leadWave: 'triangle',
    lead: [
      'E5 - - - D5 - C5 - D5 - E5 - - - . .',
      'E5 - - - D5 - C5 - A4 - - - . . . .',
      'C5 - - - A4 - C5 - D5 - C5 - A4 - . .',
      'B4 - - - C5 - D5 - - - - - . . . .',
      'G5 - - - E5 - G5 - A5 - G5 - E5 - . .',
      'E5 - - - C5 - E5 - D5 - C5 - - - . .',
      'A4 - C5 - F5 - - - E5 - D5 - C5 - . .',
      'D5 - - - - - B4 - C5 - - - - - . .',
    ].join(' | '),
    bass: rep(
      [
        'C3 - - - E3 - - - G3 - - - E3 - - -',
        'A2 - - - C3 - - - E3 - - - C3 - - -',
        'F2 - - - A2 - - - C3 - - - A2 - - -',
        'G2 - - - B2 - - - D3 - - - B2 - - -',
      ].join(' | '),
      2,
    ),
    chords: rep(
      ['C', 'Am', 'F', 'G'].map((c) => `. . . . ${c} . . . . . . . ${c} . . .`).join(' | '),
      2,
    ),
    drums: rep('k . h . s . h . k . k . s . h .', 8),
  },
  blues: {
    id: 'blues',
    title: '3AM Grill Blues',
    bpm: 84,
    swing: 0.28,
    leadWave: 'pulse25',
    lead: [
      '. . A4 C5 D5 - Eb5 E5 - - - . C5 . A4 .',
      '. . . . . . . . G4 - A4 - - - . .',
      '. . A4 C5 D5 - Eb5 E5 - - G5 - E5 . C5 .',
      'A4 - - - - - - - . . . . . . . .',
      '. . D5 F5 G5 - A5 - G5 - F5 . D5 . C5 .',
      'D5 - - - . . C5 . A4 - - - . . . .',
      '. . E5 . E5 - D5 . C5 . A4 . C5 - D5 .',
      'E5 - - - - - . . . . . . . . . .',
      'B4 - - . E5 - - . G#5 - - . B5 - - .',
      'A5 - G5 - F#5 - E5 - D5 - C5 - A4 - - .',
      '. . A4 . C5 . A4 . E5 - - - D5 - C5 .',
      'A4 - - - . . . . E4 - G#4 - B4 - D5 -',
    ].join(' | '),
    bass: ['A', 'A', 'A', 'A', 'D', 'D', 'A', 'A', 'E', 'D', 'A', 'E']
      .map((c) =>
        c === 'A'
          ? boogie('A2', 'C#3', 'E3', 'F#3', 'G3', 'F#3', 'E3', 'C#3')
          : c === 'D'
            ? boogie('D3', 'F#3', 'A3', 'B3', 'C4', 'B3', 'A3', 'F#3')
            : boogie('E2', 'G#2', 'B2', 'C#3', 'D3', 'C#3', 'B2', 'G#2'),
      )
      .join(' | '),
    chords: ['A7', 'A7', 'A7', 'A7', 'D7', 'D7', 'A7', 'A7', 'E7', 'D7', 'A7', 'E7']
      .map((c) => `${c} . . . . . . . ${c} . . . . . . .`)
      .join(' | '),
    drums: rep('k . h . s . h . k . h . s . h h', 12),
  },
  anthem: {
    id: 'anthem',
    title: 'Index Green (Anthem)',
    bpm: 144,
    swing: 0,
    leadWave: 'pulse25',
    lead: [
      'F#5 - A5 - D6 - - - C#6 - A5 - F#5 - E5 -',
      'E5 - - - C#5 - E5 - A5 - - - G5 - F#5 -',
      'F#5 - - - D5 - F#5 - B5 - - - A5 - F#5 -',
      'G5 - - - B5 - A5 - G5 - F#5 - E5 - - -',
      'F#5 - A5 - D6 - - - E6 - F#6 - E6 - D6 -',
      'C#6 - - - A5 - - - E5 - A5 - C#6 - E6 -',
      'D6 - - - B5 - - - F#5 - B5 - D6 - F#6 -',
      'E6 - - - D6 - - - C#6 - - - . . . .',
    ].join(' | '),
    bass: rep(
      ['D2', 'A2', 'B2', 'G2']
        .map((n) => {
          const up = n.replace('2', '3');
          return `${n} - ${up} - ${n} - ${up} - ${n} - ${up} - ${n} - ${up} -`;
        })
        .join(' | '),
      2,
    ),
    chords: rep(
      ['D', 'A', 'Bm', 'G'].map((c) => `${c} . . . . . ${c} . . . ${c} . . . . .`).join(' | '),
      2,
    ),
    drums: rep('k . h . s . h . k k h . s . h h', 8),
  },
  hurricane: {
    id: 'hurricane',
    title: 'Hurricane Two-Step',
    bpm: 150,
    swing: 0,
    leadWave: 'pulse12',
    lead: [
      'E5 G5 B5 G5 E5 G5 B5 G5 E6 - B5 - G5 - E5 -',
      'E5 G5 C6 G5 E5 G5 C6 G5 E6 - C6 - G5 - E5 -',
      'F#5 A5 D6 A5 F#5 A5 D6 A5 F#6 - D6 - A5 - F#5 -',
      'D#5 F#5 B5 F#5 D#5 F#5 B5 F#5 D#6 - B5 - A5 - F#5 -',
    ].join(' | '),
    bass: ['E2', 'C2', 'D2', 'B1']
      .map((n) => {
        const up = n.replace(/\d/, (d) => String(Number(d) + 1));
        return `${n} ${n} ${up} ${n} ${n} ${n} ${up} ${n} ${n} ${n} ${up} ${n} ${n} ${n} ${up} ${n}`;
      })
      .join(' | '),
    chords: ['Em', 'C', 'D', 'B7'].map((c) => `${c} . . . . . . . ${c} . . . . . . .`).join(' | '),
    drums: rep('k h s h k k s h k h s h k k s o', 4),
  },
  showdown: {
    id: 'showdown',
    title: 'Parking Lot Showdown',
    bpm: 158,
    swing: 0,
    leadWave: 'pulse25',
    lead: [
      'A4 . C5 . E5 . A5 - G5 . E5 . C5 . D5 .',
      'C5 - - . A4 . C5 . F5 - E5 . D5 . C5 .',
      'B4 . D5 . G5 - - . F5 . E5 . D5 . B4 .',
      'G#4 - - . B4 . E5 . G#5 - - - E5 . . .',
      'A5 - G5 . E5 . A5 - C6 - B5 . A5 . E5 .',
      'F5 - E5 . C5 . F5 - A5 - G5 . F5 . C5 .',
      'D5 - B4 . G4 . D5 - G5 - F5 . D5 . B4 .',
      'E5 - - - G#5 - - - B5 - - - E6 - - -',
    ].join(' | '),
    bass: rep(
      ['A', 'F', 'G', 'E']
        .map((n) => `${n}2 - ${n}3 - ${n}2 - ${n}3 - ${n}2 - ${n}3 - ${n}2 - ${n}3 -`)
        .join(' | '),
      2,
    ),
    chords: rep(
      ['Am', 'F', 'G', 'E'].map((c) => `${c} . . . . . ${c} . . . ${c} . . . . .`).join(' | '),
      2,
    ),
    drums: rep('k . h . s . h k k . h . s . h o', 8),
  },
  host: {
    id: 'host',
    title: 'The New Host',
    bpm: 58,
    swing: 0,
    leadWave: 'triangle',
    eerie: true,
    lead: ['E5 - - - F5 - - - E5 - - - Bb4 - - -', 'E5 - - - F5 - - - A5 - - - G#5 - - -'].join(
      ' | ',
    ),
    bass: rep('A1 - - - - - - - - - - - - - - -', 2),
    chords: rep('Am . . . . . . . Bb . . . . . . .', 2),
    drums: rep('k . . k . . . . . . . . . . . .', 2),
  },
};

/** What the jukebox cycles through when a customer (or you) feeds it a quarter. */
export const JUKEBOX_ORDER = ['grooved', 'raisins', 'blues', 'anthem'] as const;

export function songByTitle(title: string): Song | undefined {
  return Object.values(SONGS).find((s) => s.title === title);
}
